import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { fetchAllCustomSectionsForLocation, type CustomSection } from '../../services/customSectionsService';
import { fetchAllCustomDatasetsForLocation, type CustomDataset } from '../../services/customDatasetsService';
import { ADMIN_PAGES, AdminPageConfig } from '../../config/adminPageConfig';
import { OVERVIEW_SECTIONS, type OverviewSectionConfig } from '../../config/adminOverviewConfig';
import AdminCustomContent from '../admin/AdminCustomContent';
import './AdminSettingsPage.css';

interface SectionState {
  id: string;
  enabled: boolean;
  order: number;
  charts: ChartState[];
}

interface ChartState {
  id: string;
  enabled: boolean;
  order: number;
  includeOverview: boolean;
}

interface PageState {
  id: string;
  sections: SectionState[];
}

type OverviewSettingsState = Record<string, Record<string, boolean>>;

const adminChartIds = new Set(
  ADMIN_PAGES.flatMap((page) => page.sections.flatMap((section) => section.charts.map((chart) => chart.id)))
);

const OVERVIEW_ONLY_SECTIONS = OVERVIEW_SECTIONS.map((section) => ({
  ...section,
  charts: section.charts.filter((chart) => !adminChartIds.has(chart.id))
})).filter((section) => section.charts.length > 0);

const buildDefaultState = (): PageState[] =>
  ADMIN_PAGES.map((page) => ({
    id: page.id,
    sections: page.sections.map((section, sectionIndex) => ({
      id: section.id,
      enabled: true,
      order: sectionIndex,
      charts: section.charts.map((chart, chartIndex) => ({
        id: chart.id,
        enabled: true,
        order: chartIndex,
        includeOverview: !!chart.overviewDefault
      }))
    }))
  }));

const buildOverviewOnlyDefaults = (): OverviewSettingsState => {
  const defaults: OverviewSettingsState = {};
  OVERVIEW_ONLY_SECTIONS.forEach((section) => {
    defaults[section.id] = {};
    section.charts.forEach((chart) => {
      defaults[section.id][chart.id] = true;
    });
  });
  return defaults;
};

const DEFAULT_BLOCK_ORDER = [
  'overview',
  'transportation',
  'buildings',
  'waste',
  'people',
  'ghg_inventory',
  'policy_tool'
] as const;

const BLOCK_LABELS: Record<string, string> = {
  overview: 'Overview Page',
  custom_content: 'Custom content',
  transportation: 'Transportation',
  buildings: 'Buildings & Energy',
  waste: 'Waste +',
  people: 'People & Equity',
  ghg_inventory: 'GHG Inventory',
  policy_tool: 'Policy Tool'
};

type AdminSettingsTab = 'pages' | 'custom';

const AdminSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { hasAnyRole } = useUserRole();
  const { locationSlug } = useParams();
  const [activeTab, setActiveTab] = useState<AdminSettingsTab>('pages');
  const [settingsState, setSettingsState] = useState<PageState[]>(buildDefaultState);
  const [overviewOnlySettings, setOverviewOnlySettings] = useState<OverviewSettingsState>(
    buildOverviewOnlyDefaults
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [overviewSectionOrder, setOverviewSectionOrder] = useState<string[]>(() =>
    OVERVIEW_ONLY_SECTIONS.map((s) => s.id)
  );
  const [customSectionsList, setCustomSectionsList] = useState<CustomSection[]>([]);
  const [customDatasetsList, setCustomDatasetsList] = useState<CustomDataset[]>([]);
  const [overviewOnlyChartOrder, setOverviewOnlyChartOrder] = useState<Record<string, string[]>>(() => {
    const o: Record<string, string[]> = {};
    OVERVIEW_ONLY_SECTIONS.forEach((s) => {
      o[s.id] = s.charts.map((c) => c.id);
    });
    return o;
  });
  const customContentCount = useMemo(
    () => ({ sections: customSectionsList.length, datasets: customDatasetsList.length }),
    [customSectionsList.length, customDatasetsList.length]
  );
  const customOverviewSections = useMemo(
    () => customSectionsList.filter((s) => s.page_id === 'overview'),
    [customSectionsList]
  );
  const customOverviewDatasets = useMemo(
    () => customDatasetsList.filter((d) => d.page_id === 'overview'),
    [customDatasetsList]
  );
  const [blockOrder, setBlockOrder] = useState<string[]>(() => [...DEFAULT_BLOCK_ORDER]);

  const locationName = useMemo(() => {
    if (!locationSlug) return null;
    const resolved = supabaseLocationService.getLocationBySlug(locationSlug);
    return resolved?.name || null;
  }, [locationSlug]);

  const canEdit = hasAnyRole(['super_admin', 'local_govt_admin']);
  const userLocation = (user?.user_metadata?.specific_location || '').toLowerCase();
  const isOwnLocation = locationName ? userLocation === locationName.toLowerCase() : false;
  const canEditLocation = canEdit && (hasAnyRole(['super_admin']) || isOwnLocation);

  React.useEffect(() => {
    const loadSettings = async () => {
      if (!locationName) {
        setLoading(false);
        setOverviewOnlySettings(buildOverviewOnlyDefaults());
        return;
      }

      setLoading(true);
      const baseState = buildDefaultState();
      const overviewDefaults = buildOverviewOnlyDefaults();

      const { data: sectionRows, error: sectionError } = await supabaseClient
        .from('admin_page_sections')
        .select('page_id, section_id, is_enabled, sort_order')
        .eq('location_name', locationName);

      const { data: chartRows, error: chartError } = await supabaseClient
        .from('admin_page_charts')
        .select('page_id, section_id, chart_id, is_enabled, sort_order')
        .eq('location_name', locationName);

      const { data: overviewRows, error: overviewError } = await supabaseClient
        .from('admin_overview_settings')
        .select('section_id, chart_id, is_enabled')
        .eq('location_name', locationName);

      if (sectionError || chartError || overviewError) {
        console.error('Error loading admin settings:', sectionError || chartError || overviewError);
        setSettingsState(baseState);
        setOverviewOnlySettings(overviewDefaults);
        setLoading(false);
        return;
      }

      const nextState = baseState.map((page) => ({
        ...page,
        sections: page.sections.map((section) => {
          const sectionRow = sectionRows?.find(
            (row) => row.page_id === page.id && row.section_id === section.id
          );
          return {
            ...section,
            enabled: sectionRow ? sectionRow.is_enabled : section.enabled,
            order: sectionRow && typeof sectionRow.sort_order === 'number' ? sectionRow.sort_order : section.order,
            charts: section.charts.map((chart) => {
              const chartRow = chartRows?.find(
                (row) =>
                  row.page_id === page.id &&
                  row.section_id === section.id &&
                  row.chart_id === chart.id
              );
              const overviewRow = overviewRows?.find(
                (row) => row.section_id === section.id && row.chart_id === chart.id
              );
              return {
                ...chart,
                enabled: chartRow ? chartRow.is_enabled : chart.enabled,
                order: chartRow && typeof chartRow.sort_order === 'number' ? chartRow.sort_order : chart.order,
                includeOverview: overviewRow ? overviewRow.is_enabled : chart.includeOverview
              };
            })
          };
        })
      }));

      setSettingsState(nextState);
      const nextOverviewOnly = { ...overviewDefaults };
      (overviewRows || []).forEach((row) => {
        if (nextOverviewOnly[row.section_id] && row.chart_id in nextOverviewOnly[row.section_id]) {
          nextOverviewOnly[row.section_id][row.chart_id] = row.is_enabled;
        }
      });
      setOverviewOnlySettings(nextOverviewOnly);
      setLoading(false);
    };

    loadSettings();
  }, [locationName]);

  React.useEffect(() => {
    if (!locationName) {
      setCustomSectionsList([]);
      setCustomDatasetsList([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      fetchAllCustomSectionsForLocation(locationName),
      fetchAllCustomDatasetsForLocation(locationName)
    ]).then(([sections, datasets]) => {
      if (!cancelled) {
        setCustomSectionsList(sections);
        setCustomDatasetsList(datasets);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locationName]);

  const moveOverviewSection = useCallback((index: number, direction: 'up' | 'down') => {
    setOverviewSectionOrder((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  }, []);

  const moveBlock = useCallback((index: number, direction: 'up' | 'down') => {
    setBlockOrder((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  }, []);

  const moveOverviewChart = useCallback((sectionId: string, chartIndex: number, direction: 'up' | 'down') => {
    setOverviewOnlyChartOrder((prev) => {
      const order = prev[sectionId] ?? [];
      const next = [...order];
      const swap = direction === 'up' ? chartIndex - 1 : chartIndex + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[chartIndex], next[swap]] = [next[swap], next[chartIndex]];
      return { ...prev, [sectionId]: next };
    });
  }, []);

  const includedOnOverviewByPage = useMemo(() => {
    const byPage: Record<string, { key: string; sectionLabel: string; chartLabel: string }[]> = {};
    settingsState.forEach((page) => {
      const pageConfig = ADMIN_PAGES.find((p) => p.id === page.id);
      if (!pageConfig) return;
      const items: { key: string; sectionLabel: string; chartLabel: string }[] = [];
      page.sections.forEach((section) => {
        const sectionConfig = pageConfig.sections.find((c) => c.id === section.id);
        if (!sectionConfig) return;
        section.charts.forEach((chart) => {
          if (!chart.includeOverview) return;
          const chartConfig = sectionConfig.charts.find((c) => c.id === chart.id);
          if (!chartConfig) return;
          items.push({
            key: `${page.id}|${section.id}|${chart.id}`,
            sectionLabel: sectionConfig.label,
            chartLabel: chartConfig.label
          });
        });
      });
      if (items.length > 0) byPage[page.id] = items;
    });
    return byPage;
  }, [settingsState]);

  const overviewBlockIds = useMemo(() => {
    const ids: string[] = [];
    OVERVIEW_ONLY_SECTIONS.forEach((s) => ids.push(`overview_${s.id}`));
    Object.keys(includedOnOverviewByPage).forEach((pageId) => ids.push(`page_${pageId}`));
    if (customOverviewSections.length > 0) ids.push('custom_sections');
    if (customOverviewDatasets.length > 0) ids.push('custom_charts');
    return ids;
  }, [includedOnOverviewByPage, customOverviewSections.length, customOverviewDatasets.length]);

  const [overviewBlockOrder, setOverviewBlockOrder] = useState<string[]>([]);
  React.useEffect(() => {
    setOverviewBlockOrder((prev) => {
      const valid = new Set(overviewBlockIds);
      const kept = prev.filter((id) => valid.has(id));
      const added = overviewBlockIds.filter((id) => !prev.includes(id));
      if (kept.length === prev.length && added.length === 0) return prev;
      return [...kept, ...added];
    });
  }, [overviewBlockIds]);

  const [includedOnOverviewOrderByPage, setIncludedOnOverviewOrderByPage] = useState<Record<string, string[]>>({});
  React.useEffect(() => {
    setIncludedOnOverviewOrderByPage((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(includedOnOverviewByPage).forEach(([pageId, items]) => {
        const keys = items.map((i) => i.key);
        const current = next[pageId] ?? keys;
        const currentSet = new Set(current);
        const kept = current.filter((k) => keys.includes(k));
        const added = keys.filter((k) => !currentSet.has(k));
        const newOrder = [...kept, ...added];
        if (newOrder.join(',') !== (next[pageId] ?? []).join(',')) {
          next[pageId] = newOrder;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [includedOnOverviewByPage]);

  const moveOverviewBlock = useCallback((index: number, direction: 'up' | 'down') => {
    setOverviewBlockOrder((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  }, []);

  const moveIncludedChartInPage = useCallback((pageId: string, chartIndex: number, direction: 'up' | 'down') => {
    setIncludedOnOverviewOrderByPage((prev) => {
      const order = prev[pageId] ?? [];
      const next = [...order];
      const swap = direction === 'up' ? chartIndex - 1 : chartIndex + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[chartIndex], next[swap]] = [next[swap], next[chartIndex]];
      return { ...prev, [pageId]: next };
    });
  }, []);

  const [customOverviewSectionsOrder, setCustomOverviewSectionsOrder] = useState<string[]>([]);
  const [customOverviewDatasetsOrder, setCustomOverviewDatasetsOrder] = useState<string[]>([]);
  React.useEffect(() => {
    setCustomOverviewSectionsOrder((prev) => {
      const ids = customOverviewSections.map((s) => s.id);
      if (ids.length === 0) return [];
      const next = [...ids];
      const prevSet = new Set(prev);
      const added = ids.filter((id) => !prevSet.has(id));
      const kept = prev.filter((id) => ids.includes(id));
      return kept.length === prev.length && added.length === 0 ? prev : [...kept, ...added];
    });
  }, [customOverviewSections]);
  React.useEffect(() => {
    setCustomOverviewDatasetsOrder((prev) => {
      const ids = customOverviewDatasets.map((d) => d.id);
      if (ids.length === 0) return [];
      const next = [...ids];
      const prevSet = new Set(prev);
      const kept = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !prevSet.has(id));
      return kept.length === prev.length && added.length === 0 ? prev : [...kept, ...added];
    });
  }, [customOverviewDatasets]);

  const moveCustomOverviewSection = useCallback((index: number, direction: 'up' | 'down') => {
    setCustomOverviewSectionsOrder((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  }, []);
  const moveCustomOverviewDataset = useCallback((index: number, direction: 'up' | 'down') => {
    setCustomOverviewDatasetsOrder((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  }, []);

  const updateSection = (pageId: string, sectionId: string, updater: (section: SectionState) => SectionState) => {
    setSettingsState((prev) =>
      prev.map((page) => {
        if (page.id !== pageId) return page;
        return {
          ...page,
          sections: page.sections.map((section) =>
            section.id === sectionId ? updater(section) : section
          )
        };
      })
    );
  };

  const updateChart = (
    pageId: string,
    sectionId: string,
    chartId: string,
    updater: (chart: ChartState) => ChartState
  ) => {
    updateSection(pageId, sectionId, (section) => ({
      ...section,
      charts: section.charts.map((chart) =>
        chart.id === chartId ? updater(chart) : chart
      )
    }));
  };

  const moveItem = <T extends { order: number }>(items: T[], index: number, direction: 'up' | 'down') => {
    const next = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= next.length) return next;
    const temp = next[index];
    next[index] = next[swapIndex];
    next[swapIndex] = temp;
    return next.map((item, idx) => ({ ...item, order: idx }));
  };

  const handleSave = async () => {
    if (!locationName || !user || !canEditLocation) return;
    setSaving(true);
    setStatus(null);

    const sectionRows = settingsState.flatMap((page) =>
      page.sections.map((section) => ({
        location_name: locationName,
        page_id: page.id,
        section_id: section.id,
        is_enabled: section.enabled,
        sort_order: section.order,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      }))
    );

    const chartRows = settingsState.flatMap((page) =>
      page.sections.flatMap((section) =>
        section.charts.map((chart) => ({
          location_name: locationName,
          page_id: page.id,
          section_id: section.id,
          chart_id: chart.id,
          is_enabled: chart.enabled,
          sort_order: chart.order,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }))
      )
    );

    const overviewRows = settingsState.flatMap((page) =>
      page.sections.flatMap((section) =>
        section.charts.map((chart) => ({
          location_name: locationName,
          section_id: section.id,
          chart_id: chart.id,
          is_enabled: chart.includeOverview,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }))
      )
    );

    const overviewOnlyRows = Object.entries(overviewOnlySettings).flatMap(([sectionId, charts]) =>
      Object.entries(charts).map(([chartId, isEnabled]) => ({
        location_name: locationName,
        section_id: sectionId,
        chart_id: chartId,
        is_enabled: isEnabled,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      }))
    );

    const { error: sectionError } = await supabaseClient
      .from('admin_page_sections')
      .upsert(sectionRows, { onConflict: 'location_name,page_id,section_id' });

    const { error: chartError } = await supabaseClient
      .from('admin_page_charts')
      .upsert(chartRows, { onConflict: 'location_name,page_id,section_id,chart_id' });

    const { error: overviewError } = await supabaseClient
      .from('admin_overview_settings')
      .upsert([...overviewRows, ...overviewOnlyRows], { onConflict: 'location_name,section_id,chart_id' });

    if (sectionError || chartError || overviewError) {
      console.error('Error saving admin settings:', sectionError || chartError || overviewError);
      setStatus('Unable to save changes. Please try again.');
      setSaving(false);
      return;
    }

    setStatus('Settings saved.');
    setSaving(false);
  };

  if (!canEdit) {
    return (
      <div className="admin-settings-page">
        <div className="admin-settings-banner">You do not have permission to access Admin Settings.</div>
      </div>
    );
  }

  if (!locationName) {
    return (
      <div className="admin-settings-page">
        <div className="admin-settings-banner">Select a location to manage Admin Settings.</div>
      </div>
    );
  }

  if (!canEditLocation) {
    return (
      <div className="admin-settings-page">
        <div className="admin-settings-banner">
          You can only edit Admin Settings for your assigned location.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <div className="admin-settings-header">
        <div>
          <h1>Admin Settings</h1>
          <p>Configure which sections and charts appear across the app.</p>
        </div>
        <div className="admin-settings-location">
          <strong>Location:</strong> {locationName}
        </div>
      </div>

      <div className="admin-settings-tabs">
        <button
          type="button"
          className={`admin-settings-tab ${activeTab === 'pages' ? 'active' : ''}`}
          onClick={() => setActiveTab('pages')}
        >
          Pages &amp; charts
        </button>
        <button
          type="button"
          className={`admin-settings-tab ${activeTab === 'custom' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom content
        </button>
      </div>

      {loading ? (
        <div className="admin-settings-loading">Loading settings...</div>
      ) : (
        <div className="admin-settings-content">
          {activeTab === 'pages' && (
            <>
          {blockOrder.map((blockId, blockIndex) => {
            if (blockId === 'overview') {
              const showOverviewBlock = overviewBlockOrder.length > 0;
              if (!showOverviewBlock) return null;
              return (
                <div key="overview" className="admin-settings-page-block">
                  <div className="admin-settings-block-header">
                    <h2>{BLOCK_LABELS.overview}</h2>
                    <div className="admin-settings-order-buttons">
                      <button type="button" disabled={blockIndex === 0} onClick={() => moveBlock(blockIndex, 'up')} aria-label="Move section up">↑</button>
                      <button type="button" disabled={blockIndex === blockOrder.length - 1} onClick={() => moveBlock(blockIndex, 'down')} aria-label="Move section down">↓</button>
                    </div>
                  </div>
                  {overviewBlockOrder.map((obId, obIndex) => {
                    if (obId.startsWith('overview_')) {
                      const sectionId = obId.replace(/^overview_/, '');
                      const section = OVERVIEW_ONLY_SECTIONS.find((s) => s.id === sectionId);
                      if (!section) return null;
                      const sectionSettings = overviewOnlySettings[section.id] || {};
                      const chartOrder = overviewOnlyChartOrder[section.id] ?? section.charts.map((c) => c.id);
                      const orderedCharts = chartOrder
                        .map((id) => section.charts.find((c) => c.id === id))
                        .filter((c): c is { id: string; label: string } => c != null);
                      return (
                        <div key={obId} className="admin-settings-section">
                          <div className="admin-settings-section-header">
                            <span className="admin-settings-section-title">{section.label}</span>
                            <div className="admin-settings-order-buttons">
                              <button type="button" disabled={obIndex === 0} onClick={() => moveOverviewBlock(obIndex, 'up')} aria-label="Move section up">↑</button>
                              <button type="button" disabled={obIndex === overviewBlockOrder.length - 1} onClick={() => moveOverviewBlock(obIndex, 'down')} aria-label="Move section down">↓</button>
                            </div>
                          </div>
                          <div className="admin-settings-chart-list">
                            {orderedCharts.map((chart, chartIndex) => (
                              <div key={chart.id} className="admin-settings-chart-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={!!sectionSettings[chart.id]}
                                    onChange={(event) =>
                                      setOverviewOnlySettings((prev) => ({
                                        ...prev,
                                        [section.id]: {
                                          ...(prev[section.id] || {}),
                                          [chart.id]: event.target.checked
                                        }
                                      }))
                                  }
                                />
                                <span>{chart.label}</span>
                              </label>
                                <div className="admin-settings-order-buttons">
                                  <button type="button" disabled={chartIndex === 0} onClick={() => moveOverviewChart(section.id, chartIndex, 'up')} aria-label="Move chart up">↑</button>
                                  <button type="button" disabled={chartIndex === orderedCharts.length - 1} onClick={() => moveOverviewChart(section.id, chartIndex, 'down')} aria-label="Move chart down">↓</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    if (obId.startsWith('page_')) {
                      const pageId = obId.replace(/^page_/, '');
                      const pageConfig = ADMIN_PAGES.find((p) => p.id === pageId);
                      const items = includedOnOverviewByPage[pageId] ?? [];
                      const order = includedOnOverviewOrderByPage[pageId] ?? items.map((i) => i.key);
                      const orderedItems = order
                        .map((key) => items.find((i) => i.key === key))
                        .filter((i): i is NonNullable<typeof i> => i != null);
                      if (!pageConfig || orderedItems.length === 0) return null;
                      return (
                        <div key={obId} className="admin-settings-section">
                          <div className="admin-settings-section-header">
                            <span className="admin-settings-section-title">{pageConfig.label}</span>
                            <div className="admin-settings-order-buttons">
                              <button type="button" disabled={obIndex === 0} onClick={() => moveOverviewBlock(obIndex, 'up')} aria-label="Move section up">↑</button>
                              <button type="button" disabled={obIndex === overviewBlockOrder.length - 1} onClick={() => moveOverviewBlock(obIndex, 'down')} aria-label="Move section down">↓</button>
                            </div>
                          </div>
                          <div className="admin-settings-chart-list">
                            {orderedItems.map((item, chartIndex) => (
                              <div key={item.key} className="admin-settings-chart-item">
                                <span className="admin-settings-section-title">{item.sectionLabel} → {item.chartLabel}</span>
                                <div className="admin-settings-order-buttons">
                                  <button type="button" disabled={chartIndex === 0} onClick={() => moveIncludedChartInPage(pageId, chartIndex, 'up')} aria-label="Move up">↑</button>
                                  <button type="button" disabled={chartIndex === orderedItems.length - 1} onClick={() => moveIncludedChartInPage(pageId, chartIndex, 'down')} aria-label="Move down">↓</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    if (obId === 'custom_sections') {
                      return (
                        <div key={obId} className="admin-settings-section">
                          <div className="admin-settings-section-header">
                            <span className="admin-settings-section-title">Custom sections (Overview)</span>
                            <div className="admin-settings-order-buttons">
                              <button type="button" disabled={obIndex === 0} onClick={() => moveOverviewBlock(obIndex, 'up')} aria-label="Move up">↑</button>
                              <button type="button" disabled={obIndex === overviewBlockOrder.length - 1} onClick={() => moveOverviewBlock(obIndex, 'down')} aria-label="Move down">↓</button>
                            </div>
                          </div>
                          <div className="admin-settings-chart-list">
                            {customOverviewSectionsOrder
                              .map((id) => customOverviewSections.find((s) => s.id === id))
                              .filter((s): s is CustomSection => s != null)
                              .map((section, idx) => (
                                <div key={section.id} className="admin-settings-chart-item">
                                  <span>{section.section_title}</span>
                                  <div className="admin-settings-order-buttons">
                                    <button type="button" disabled={idx === 0} onClick={() => moveCustomOverviewSection(idx, 'up')} aria-label="Move up">↑</button>
                                    <button type="button" disabled={idx === customOverviewSectionsOrder.length - 1} onClick={() => moveCustomOverviewSection(idx, 'down')} aria-label="Move down">↓</button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    }
                    if (obId === 'custom_charts') {
                      return (
                        <div key={obId} className="admin-settings-section">
                          <div className="admin-settings-section-header">
                            <span className="admin-settings-section-title">Custom charts (Overview)</span>
                            <div className="admin-settings-order-buttons">
                              <button type="button" disabled={obIndex === 0} onClick={() => moveOverviewBlock(obIndex, 'up')} aria-label="Move up">↑</button>
                              <button type="button" disabled={obIndex === overviewBlockOrder.length - 1} onClick={() => moveOverviewBlock(obIndex, 'down')} aria-label="Move down">↓</button>
                            </div>
                          </div>
                          <div className="admin-settings-chart-list">
                            {customOverviewDatasetsOrder
                              .map((id) => customOverviewDatasets.find((d) => d.id === id))
                              .filter((d): d is CustomDataset => d != null)
                              .map((dataset, idx) => (
                                <div key={dataset.id} className="admin-settings-chart-item">
                                  <span>{dataset.title}</span>
                                  <div className="admin-settings-order-buttons">
                                    <button type="button" disabled={idx === 0} onClick={() => moveCustomOverviewDataset(idx, 'up')} aria-label="Move up">↑</button>
                                    <button type="button" disabled={idx === customOverviewDatasetsOrder.length - 1} onClick={() => moveCustomOverviewDataset(idx, 'down')} aria-label="Move down">↓</button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              );
            }
            if (blockId === 'custom_content') return null;
            const pageConfig = ADMIN_PAGES.find((p) => p.id === blockId) as AdminPageConfig | undefined;
            if (!pageConfig) return null;
            const pageState = settingsState.find((page) => page.id === pageConfig.id);
            if (!pageState) return null;
            const orderedSections = [...pageState.sections].sort((a, b) => a.order - b.order);

            return (
              <div key={pageConfig.id} className="admin-settings-page-block">
                <div className="admin-settings-block-header">
                  <h2>{pageConfig.label}</h2>
                  <div className="admin-settings-order-buttons">
                    <button type="button" disabled={blockIndex === 0} onClick={() => moveBlock(blockIndex, 'up')} aria-label="Move section up">↑</button>
                    <button type="button" disabled={blockIndex === blockOrder.length - 1} onClick={() => moveBlock(blockIndex, 'down')} aria-label="Move section down">↓</button>
                  </div>
                </div>
                {orderedSections.map((section, sectionIndex) => {
                  const sectionConfig = pageConfig.sections.find((cfg) => cfg.id === section.id);
                  if (!sectionConfig) return null;
                  const orderedCharts = [...section.charts].sort((a, b) => a.order - b.order);

                  return (
                    <div key={section.id} className="admin-settings-section">
                      <div className="admin-settings-section-header">
                        <label className="admin-settings-section-title">
                          <input
                            type="checkbox"
                            checked={section.enabled}
                            onChange={(event) =>
                              updateSection(pageConfig.id, section.id, (current) => ({
                                ...current,
                                enabled: event.target.checked
                              }))
                            }
                          />
                          <span>{sectionConfig.label}</span>
                        </label>
                        <div className="admin-settings-order-buttons">
                          <button
                            type="button"
                            disabled={sectionIndex === 0}
                            onClick={() =>
                              updateSection(pageConfig.id, section.id, () => {
                                const nextSections = moveItem(orderedSections, sectionIndex, 'up');
                                setSettingsState((prev) =>
                                  prev.map((page) =>
                                    page.id === pageConfig.id
                                      ? { ...page, sections: nextSections }
                                      : page
                                  )
                                );
                                return section;
                              })
                            }
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            disabled={sectionIndex === orderedSections.length - 1}
                            onClick={() =>
                              updateSection(pageConfig.id, section.id, () => {
                                const nextSections = moveItem(orderedSections, sectionIndex, 'down');
                                setSettingsState((prev) =>
                                  prev.map((page) =>
                                    page.id === pageConfig.id
                                      ? { ...page, sections: nextSections }
                                      : page
                                  )
                                );
                                return section;
                              })
                            }
                          >
                            ↓
                          </button>
                        </div>
                      </div>

                      <div className="admin-settings-chart-list">
                        {orderedCharts.map((chart, chartIndex) => {
                          const chartConfig = sectionConfig.charts.find((cfg) => cfg.id === chart.id);
                          if (!chartConfig) return null;
                          return (
                            <div key={chart.id} className="admin-settings-chart-item">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={chart.enabled}
                                  onChange={(event) =>
                                    updateChart(pageConfig.id, section.id, chart.id, (current) => ({
                                      ...current,
                                      enabled: event.target.checked
                                    }))
                                  }
                                />
                                <span>{chartConfig.label}</span>
                              </label>
                              <label className="admin-settings-overview-toggle">
                                <input
                                  type="checkbox"
                                  checked={chart.includeOverview}
                                  onChange={(event) =>
                                    updateChart(pageConfig.id, section.id, chart.id, (current) => ({
                                      ...current,
                                      includeOverview: event.target.checked
                                    }))
                                  }
                                />
                                <span>Include on Overview</span>
                              </label>
                              <div className="admin-settings-order-buttons">
                                <button
                                  type="button"
                                  disabled={chartIndex === 0}
                                  onClick={() =>
                                    updateSection(pageConfig.id, section.id, (current) => ({
                                      ...current,
                                      charts: moveItem(orderedCharts, chartIndex, 'up')
                                    }))
                                  }
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  disabled={chartIndex === orderedCharts.length - 1}
                                  onClick={() =>
                                    updateSection(pageConfig.id, section.id, (current) => ({
                                      ...current,
                                      charts: moveItem(orderedCharts, chartIndex, 'down')
                                    }))
                                  }
                                >
                                  ↓
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
            </>
          )}
          {activeTab === 'custom' && user && (
            <AdminCustomContent locationName={locationName} userId={user.id} />
          )}
        </div>
      )}

      <div className="admin-settings-footer">
        {status && <div className="admin-settings-status">{status}</div>}
        <button type="button" className="admin-settings-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
